/**
 * Composant d'affichage quand un quota est atteint
 * Affiche un message avec proposition d'upgrade vers un plan supérieur
 */
import { AlertTriangle, Sparkles, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface QuotaExceededBannerProps {
  type: 'analyse_ia' | 'ocr';
  /** Variante d'affichage */
  variant?: 'banner' | 'inline' | 'card';
  /** Classe CSS additionnelle */
  className?: string;
}

export function QuotaExceededBanner({
  type,
  variant = 'banner',
  className = '',
}: QuotaExceededBannerProps) {
  const navigate = useNavigate();

  const config = {
    analyse_ia: {
      title: 'Quota d\'analyses IA atteint',
      description: 'Vous avez utilisé toutes vos analyses IA ce mois-ci.',
      cta: 'Passez au plan supérieur pour continuer à analyser vos chantiers.',
    },
    ocr: {
      title: 'Quota de scans IA atteint',
      description: 'Vous avez utilisé tous vos scans IA ce mois-ci.',
      cta: 'Passez au plan supérieur pour continuer à importer des documents.',
    },
  };

  const { title, description, cta } = config[type];

  const handleUpgrade = () => {
    navigate('/settings/subscription');
  };

  // Variante banner (pleine largeur)
  if (variant === 'banner') {
    return (
      <div className={`p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg ${className}`}>
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-600 dark:text-amber-400">{title}</p>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
            <p className="text-sm text-muted-foreground">{cta}</p>
            <Button
              variant="default"
              size="sm"
              className="mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              onClick={handleUpgrade}
            >
              <Crown className="w-4 h-4 mr-2" />
              Voir les plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Variante inline (compacte)
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg ${className}`}>
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="text-sm font-medium text-amber-600 dark:text-amber-400 flex-1">{title}</span>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
          onClick={handleUpgrade}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Upgrade
        </Button>
      </div>
    );
  }

  // Variante card (avec plus de détails)
  return (
    <div className={`p-6 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl ${className}`}>
      <div className="flex flex-col items-center text-center">
        <div className="p-3 bg-amber-500/20 rounded-full mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
        </div>
        <h3 className="text-lg font-bold text-amber-600 dark:text-amber-400">{title}</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">{description}</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">{cta}</p>
        <Button
          variant="default"
          size="lg"
          className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          onClick={handleUpgrade}
        >
          <Crown className="w-5 h-5 mr-2" />
          Passer au plan supérieur
        </Button>
      </div>
    </div>
  );
}

export default QuotaExceededBanner;
